'use client';

import * as React from 'react';
import { Check, Copy } from 'lucide-react';

import { MCP_CONNECT, MCP_ENDPOINT } from '~/lib/mcp-content';

export function McpEndpointCard({
  compact = false
}: {
  compact?: boolean;
}): React.JSX.Element {
  const [copied, setCopied] = React.useState(false);
  const [copyError, setCopyError] = React.useState(false);
  const resetTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(
    () => () => {
      if (resetTimer.current) clearTimeout(resetTimer.current);
    },
    []
  );

  const handleCopy = React.useCallback(async () => {
    setCopyError(false);
    try {
      await navigator.clipboard.writeText(MCP_ENDPOINT);
      setCopied(true);
      if (resetTimer.current) clearTimeout(resetTimer.current);
      resetTimer.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
      setCopyError(true);
    }
  }, []);

  return (
    <div
      className={
        compact
          ? undefined
          : 'surface-card rounded-2xl border border-[color:var(--hairline)] bg-[color:var(--paper)] p-4 md:p-5'
      }
    >
      <p className="eyebrow mb-3">{MCP_CONNECT.endpointLabel}</p>
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-[color:var(--hairline-2)] bg-[color:var(--paper-2)] px-4 py-3">
        <code className="min-w-0 flex-1 select-all break-all font-mono text-[13px] md:text-[14px] text-[color:var(--ink)]">
          {MCP_ENDPOINT}
        </code>
        <button
          type="button"
          onClick={handleCopy}
          aria-label="Copy MCP server URL"
          className="inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded-lg bg-teal-500 px-3 py-1.5 text-[12.5px] font-medium text-[#001A12] transition-colors hover:bg-teal-400 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-teal-700"
        >
          {copied ? (
            <Check
              aria-hidden
              className="h-3.5 w-3.5"
            />
          ) : (
            <Copy
              aria-hidden
              className="h-3.5 w-3.5"
            />
          )}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <p
        role="status"
        aria-live="polite"
        className="mt-2 min-h-4 text-xs text-[color:var(--ink-3)]"
      >
        {copyError
          ? 'Could not copy. Select the URL above and copy it manually.'
          : copied
            ? 'Server URL copied.'
            : ''}
      </p>
    </div>
  );
}
